import React from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { fileUrl } from "@/lib/api";
import { useLightbox } from "@/context/LightboxContext";
import LogoutConfirmDialog from "@/components/app/LogoutConfirmDialog";
import {
  Flame,
  Zap,
  Video,
  MessageCircle,
  Users,
  User,
  Heart,
  Sparkles,
  LogOut,
} from "lucide-react";

/**
 * DesktopSidebar — persistent left rail shown on `lg:` (≥1024px) only.
 *
 * Goal: stop "phone-stranded-in-the-middle" syndrome on laptops. The sidebar
 * replaces the floating bottom nav on desktop, surfaces the user identity
 * card, and gives a single click to every primary route — exactly what a
 * Chrome/desktop user expects from a chat product (WhatsApp Web pattern).
 *
 * Mobile is untouched: this component is hidden via `hidden lg:flex`.
 */
const navItems = [
  { to: "/discover", labelKey: "nav.discover", icon: Flame, testid: "side-nav-discover" },
  { to: "/tonight", labelKey: "nav.tonight", icon: Zap, testid: "side-nav-tonight" },
  { to: "/moments", labelKey: "nav.moments", icon: Video, testid: "side-nav-moments" },
  { to: "/chats", labelKey: "nav.chats", icon: MessageCircle, testid: "side-nav-chats" },
  { to: "/groups", labelKey: "nav.groups", icon: Users, testid: "side-nav-groups" },
  { to: "/likes", labelKey: "nav.likes", icon: Heart, testid: "side-nav-likes", fallback: "Reacted" },
  { to: "/profile", labelKey: "nav.profile", icon: User, testid: "side-nav-profile" },
];

export default function DesktopSidebar() {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const { openLightbox } = useLightbox();

  // Only show on app routes (user is logged in). Landing/auth pages keep the
  // full bleed marketing layout.
  if (!user) return null;

  // Hide on certain immersive flows where the rail would intrude.
  // NOTE: `/chats/:matchId` and `/groups/:groupId` are NOT hidden — on desktop
  // they render inside the multi-pane workspace and the sidebar stays visible.
  const hiddenOn = [
    /^\/moments\/new$/,
    /^\/onboarding$/,
    /^\/verify-email$/,
    /^\/verify$/,
  ];
  if (hiddenOn.some((rx) => rx.test(location.pathname))) return null;

  const photo = user.photos?.[0]?.storage_path;
  const firstName = (user.name || "").split(" ")[0] || "You";

  return (
    <aside
      data-testid="desktop-sidebar"
      className="hidden lg:flex fixed left-0 top-0 bottom-0 z-30 w-[232px] xl:w-[252px] flex-col border-r border-white/[0.06] bg-[#0F1012]/85 backdrop-blur-xl"
    >
      {/* Brand */}
      <div className="px-6 pt-7 pb-6">
        <Link
          to="/discover"
          data-testid="side-nav-brand"
          className="flex items-baseline gap-1.5 group"
        >
          <span
            className="text-[26px] leading-none tracking-[-0.02em] text-[#F0E6D2] group-hover:text-[#C9A961] transition-colors"
            style={{ fontFamily: '"Fraunces", "Cormorant Garamond", serif', fontWeight: 500 }}
          >
            Spark
          </span>
          <Sparkles className="w-4 h-4 text-[#C9A961]" strokeWidth={1.5} />
        </Link>
        <div className="mt-1 text-[10px] uppercase tracking-[0.28em] text-[#6E6A62]">
          Editorial Dating
        </div>
      </div>

      {/* Identity card. The avatar opens the user's own photos in a lightbox
          (so they can see themselves big); the rest of the card navigates to
          /profile. Two separate click targets keeps both actions discoverable. */}
      <div className="mx-4 mb-5 flex items-center gap-3 rounded-2xl border border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.04] px-3 py-2.5 transition-colors">
        <button
          type="button"
          onClick={() => {
            const all = (user.photos || []).map((p) => p.storage_path).filter(Boolean);
            if (all.length > 0) {
              // fileUrl import is in @/lib/api but we already have storage paths;
              // openLightbox accepts full URLs so we construct them inline.
              openLightbox(
                all.map((sp) => {
                  // mimic fileUrl: prefer absolute path through /api/files
                  if (/^https?:\/\//.test(sp)) return sp;
                  return `${process.env.REACT_APP_BACKEND_URL}/api/files/${sp}`;
                }),
                0
              );
            }
          }}
          data-testid="side-nav-avatar"
          className="relative w-11 h-11 rounded-full overflow-hidden bg-[#25252A] flex-shrink-0 ring-1 ring-[#C9A961]/30 cursor-zoom-in"
          title="View your photos"
          aria-label="View your photos"
        >
          {photo ? (
            <img
              src={fileUrl(photo, 320)}
              alt={firstName}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-[#8C867C]">
              <User className="w-5 h-5" strokeWidth={1.5} />
            </div>
          )}
        </button>
        <Link
          to="/profile"
          data-testid="side-nav-identity"
          className="min-w-0 flex-1"
        >
          <div className="text-[14px] text-[#F0E6D2] font-medium truncate">
            {firstName}
          </div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#8C867C]">
            View profile
          </div>
        </Link>
      </div>

      {/* Primary nav */}
      <nav className="flex-1 px-3 space-y-0.5 overflow-y-auto">
        {navItems.map(({ to, labelKey, icon: Icon, testid, fallback }) => (
          <NavLink
            key={to}
            to={to}
            data-testid={testid}
            className={({ isActive }) =>
              [
                "group relative flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors",
                isActive
                  ? "bg-[#C9A961]/12 text-[#F0E6D2]"
                  : "text-[#8C867C] hover:text-[#F0E6D2] hover:bg-white/[0.03]",
              ].join(" ")
            }
          >
            {({ isActive }) => (
              <>
                {isActive && (
                  <span className="absolute left-0 top-1/2 -translate-y-1/2 h-5 w-[2px] rounded-r-full bg-[#C9A961]" />
                )}
                <Icon
                  className={`w-[18px] h-[18px] ${isActive ? "text-[#C9A961]" : ""}`}
                  strokeWidth={isActive ? 2.2 : 1.7}
                />
                <span className="text-[13.5px] tracking-wide">
                  {t(labelKey, { defaultValue: fallback || labelKey })}
                </span>
              </>
            )}
          </NavLink>
        ))}
      </nav>

      {/* Footer */}
      <div className="px-4 py-4 border-t border-white/[0.05] flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-[0.22em] text-[#6E6A62]">
          v5.1
        </span>
        <LogoutConfirmDialog onConfirm={logout}>
          <button
            type="button"
            data-testid="side-nav-logout"
            className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-[#8C867C] hover:text-[#F0E6D2] transition-colors"
          >
            <LogOut className="w-3.5 h-3.5" strokeWidth={1.7} />
            Sign out
          </button>
        </LogoutConfirmDialog>
      </div>
    </aside>
  );
}
